import { getToken, onMessage } from 'firebase/messaging';
import { messaging, auth } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { User } from '../models/User';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

console.log('VAPID Key loaded:', VAPID_KEY ? 'Yes' : 'No');
if (VAPID_KEY) {
  console.log('VAPID Key length:', VAPID_KEY.length);
  console.log('VAPID Key preview:', VAPID_KEY.substring(0, 20) + '...');
}

const functions = getFunctions();
const registerFCMToken = httpsCallable(functions, 'registerFCMToken');

export const requestNotificationPermission = async (user: User) => {
  console.log('Requesting notification permission for user:', user.id);
  
  if (!('Notification' in window)) {
    console.log('This browser does not support desktop notification');
    return;
  }

  try {
    console.log('Current notification permission:', Notification.permission);

    const permission = await Notification.requestPermission();
    console.log('Notification permission result:', permission);

    if (permission === 'granted') {
      // Wait longer to ensure Firebase Auth context is fully established
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Ensure user is authenticated and get fresh ID token
      if (!auth.currentUser) {
        console.log('User not authenticated, skipping FCM token request');
        return;
      }

      try {
        console.log('Getting FCM token with VAPID key...');
        
        // Force refresh the ID token to ensure authentication context
        console.log('🔧 Refreshing Firebase Auth token for FCM...');
        await auth.currentUser.getIdToken(true);
        console.log('✅ Firebase Auth token refreshed');
        
        // Get token with proper error handling and retry logic
        let currentToken = null;
        let retryCount = 0;
        const maxRetries = 3;
        
        while (!currentToken && retryCount < maxRetries) {
          try {
            console.log(`🔄 FCM token attempt ${retryCount + 1}/${maxRetries}...`);
            
            currentToken = await getToken(messaging, { 
              vapidKey: VAPID_KEY,
              serviceWorkerRegistration: await navigator.serviceWorker.ready
            });
            
            if (currentToken) {
              console.log('✅ FCM token result: Success');
              console.log('FCM Token received:', currentToken.substring(0, 20) + '...');
              
              // Send the token to your server and update the user's profile
              console.log('Registering FCM token with server...');
              const result = await registerFCMToken({ userId: user.id, token: currentToken });
              console.log('FCM Token registration result:', result);
              console.log('✅ FCM Token registered successfully');
              return; // Success, exit early
            }
          } catch (attemptError: any) {
            console.warn(`❌ FCM token attempt ${retryCount + 1} failed:`, attemptError?.message || attemptError);
            retryCount++;
            
            if (retryCount < maxRetries) {
              // Wait before retry, with exponential backoff
              const delay = 1000 * Math.pow(2, retryCount);
              console.log(`⏱️ Waiting ${delay}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              
              // Refresh auth token before retry
              if (auth.currentUser) {
                await auth.currentUser.getIdToken(true);
              }
            }
          }
        }
        
        if (!currentToken) {
          console.log('❌ No registration token available after all retries');
        }
        
      } catch (err: any) {
        console.warn('FCM token registration failed (non-blocking):', err);
        
        // Provide more specific error information
        if (err?.code === 'messaging/token-subscribe-failed') {
          console.warn('FCM token subscription failed - this is often temporary and non-critical');
        } else if (err?.code === 'messaging/permission-blocked') {
          console.warn('FCM permission blocked by user');
        } else if (err?.code === 'messaging/unsupported-browser') {
          console.warn('FCM not supported in this browser');
        } else {
          console.warn('Unknown FCM error:', err?.message || err);
        }
        
        // Don't throw - FCM failure shouldn't block the app
      }
    } else {
      console.log('Notification permission denied or dismissed');
    }
  } catch (err) {
    console.warn('Notification permission request failed (non-blocking):', err);
    // Don't throw - notification failure shouldn't block the app
  }
};

export const onMessageListener = () =>
  new Promise((resolve) => {
    onMessage(messaging, (payload) => {
      console.log('Received foreground message:', payload);
      resolve(payload);
    });
  }); 